import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronDown, FileText, Plus, Sparkles } from 'lucide-react';
import { useAuth } from '@/app/providers';
import { ROUTES } from '@/config/constants';
import { cn } from '@/lib/utils';
import { useMyCommunities } from '@/features/communities/hooks/useMyCommunities';
import { CreateCommunityModal } from '@/features/communities/components/CreateCommunityModal';
import { useMyThemes } from './hooks/useThemeDoc';

/**
 * Theme-Studio document switcher (à la Claude's project/folder dropdown): pick one
 * of the communities you own to theme it with its real persistent data, reopen one
 * of your saved theme docs, jump to a blank theme, or spin up a new community.
 */
export function StudioCommunitySwitcher({
  currentId,
  currentThemeId,
  currentLabel,
}: {
  currentId?: string;
  currentThemeId?: string;
  currentLabel: string;
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: mine = [] } = useMyCommunities();
  const { data: myThemes = [] } = useMyThemes(Boolean(user));
  const drafts = myThemes.filter((t) => !t.community_id);
  const owned = mine.filter((m) => m.relationship === 'owner');
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const label = currentId
    ? (owned.find((m) => m.community.id === currentId)?.community.name ?? currentLabel)
    : currentThemeId
      ? currentLabel
      : 'Blank theme';

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.04] px-2.5 py-1.5 text-[12px] font-bold text-hi transition-colors hover:bg-white/[0.08]"
      >
        <span className="max-w-[160px] truncate">{label}</span>
        <ChevronDown size={14} className={cn('flex-none text-mid transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 top-full z-50 mt-1.5 w-64 overflow-hidden rounded-xl border border-white/12 bg-ink-800 p-1.5 shadow-[0_24px_60px_rgba(0,0,0,0.6)]"
        >
          <div className="px-2.5 pb-1 pt-1.5 text-[10px] font-bold uppercase tracking-[0.6px] text-lo">
            Your communities
          </div>

          {user && owned.length > 0 ? (
            owned.map((m) => {
              const active = m.community.id === currentId;
              return (
                <button
                  key={m.community.id}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    navigate(ROUTES.themeStudioFor(m.community.id));
                  }}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] font-semibold transition-colors',
                    active ? 'bg-accent/15 text-accent' : 'text-hi hover:bg-white/5',
                  )}
                >
                  <span
                    className="grid h-6 w-6 flex-none place-items-center rounded-md text-[11px] font-black text-black"
                    style={{ background: m.community.color ?? '#54b9fe' }}
                  >
                    {(m.community.name ?? 'C').charAt(0).toUpperCase()}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{m.community.name}</span>
                  {active && <Check size={14} className="flex-none" />}
                </button>
              );
            })
          ) : (
            <p className="px-2.5 pb-2 pt-1 text-[12px] leading-relaxed text-lo">
              {user ? "You don't own a community yet — create one to theme it." : 'Sign in to see your communities.'}
            </p>
          )}

          {drafts.length > 0 && (
            <>
              <div className="mx-1.5 my-1 h-px bg-white/[0.06]" />
              <div className="px-2.5 pb-1 pt-1.5 text-[10px] font-bold uppercase tracking-[0.6px] text-lo">
                Your themes
              </div>
              {drafts.slice(0, 6).map((t) => {
                const active = t.id === currentThemeId;
                return (
                  <button
                    key={t.id}
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setOpen(false);
                      navigate(`${ROUTES.themeStudio}?t=${t.id}`);
                    }}
                    className={cn(
                      'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] font-semibold transition-colors',
                      active ? 'bg-accent/15 text-accent' : 'text-hi hover:bg-white/5',
                    )}
                  >
                    <FileText size={14} className={cn('flex-none', active ? 'text-accent' : 'text-mid')} />
                    <span className="min-w-0 flex-1 truncate">{t.name}</span>
                    {t.status === 'published' && (
                      <span className="flex-none rounded-pill bg-white/10 px-1.5 py-0.5 text-[9px] font-bold uppercase text-mid">
                        Live
                      </span>
                    )}
                    {active && <Check size={14} className="flex-none" />}
                  </button>
                );
              })}
            </>
          )}

          <div className="mx-1.5 my-1 h-px bg-white/[0.06]" />

          {(currentId || currentThemeId) && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                navigate(ROUTES.themeStudio);
              }}
              className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] font-semibold text-hi transition-colors hover:bg-white/5"
            >
              <Sparkles size={15} className="flex-none text-mid" /> Blank theme
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              setCreateOpen(true);
            }}
            className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] font-semibold text-hi transition-colors hover:bg-white/5"
          >
            <Plus size={15} className="flex-none text-accent" /> Create a community
          </button>
        </div>
      )}

      <CreateCommunityModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}
